import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import ShopLogoSvg from '~/components/icons/ShopLogoSvg';
import { useRoutePath } from '~/hooks/useRoutePath';

type Props = {
  size?: {
    width: number;
    height: number;
  };
  variant?: 'light' | 'dark';
  className?: string;
  /** External URL to link to instead of the shop homepage. */
  href?: string;
  /** Renders the small "e-shop" wordmark below the logo. */
  showEshopText?: boolean;
  /**
   * Real, screen-reader-visible text rendered inside the link. When set it becomes the
   * link's accessible name (so the generic aria-label is dropped) and gives the wrapping
   * element real text — this is how the homepage logo doubles as the <h1>. The SVG is
   * already aria-hidden, so there is no competing name.
   */
  srText?: string;
};

const ShopLogo = ({ size, variant = 'light', className = '', href, showEshopText = true, srText }: Props) => {
  const routePath = useRoutePath();
  const width = size?.width ?? 96;
  const height = size?.height ?? 36;
  const { t } = useTranslation();

  const image = (
    <ShopLogoSvg
      width={width}
      height={height}
      variant={variant}
      showEshopText={showEshopText}
      className="d-block w-auto"
    />
  );

  const srLabel = srText ? <span className="visually-hidden">{srText}</span> : null;

  if (href) {
    return (
      <a
        href={href}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={srText ? undefined : t('company_logo')}
      >
        {image}
        {srLabel}
      </a>
    );
  }

  return (
    <Link to={routePath('homepage')} className={className} aria-label={srText ? undefined : t('go_home')}>
      {image}
      {srLabel}
    </Link>
  );
};

export default ShopLogo;
